<template>
    <Dialog
        :value="true"
        :header-text="account_member && account_member.person_id ? $t('Edit member') : $t('Add member')"
        persistent
        fixed
        @close_modal="$emit('close')"
    >
        <form
            v-if="account_member"
            class="mt-n4"
        >
            <v-row
                no-gutters
            >
                <v-col cols="12">
                    <v-radio-group
                        v-model="account_member.status"
                        class="mt-2 pt-0"
                        row
                    >
                        <v-row no-gutters>
                            <v-col
                                v-for="statusOption in statusOptions"
                                :key="statusOption.value"
                                cols="6"
                            >
                                <v-radio
                                    :value="statusOption.value"
                                >
                                    <template #label>
                                        <div>
                                            <div class="body-2 font-weight-bold mb-1">{{ statusOption.name }}</div>
                                            <div class="body-2">
                                                <div
                                                    v-for="(description, index) in statusOption.description"
                                                    :key="index"
                                                >
                                                    {{ description }}
                                                </div>
                                            </div>
                                        </div>
                                    </template>
                                </v-radio>
                            </v-col>
                        </v-row>
                    </v-radio-group>
                </v-col>
                <v-col cols="12">
                    <v-text-field
                        v-model="account_member.email"
                        :error-messages="account_member.email_error"
                        :label="$t('Email')"
                        :disabled="!canEditEmail"
                        :append-icon="!canEditEmail ? 'mdi-lock' : ''"
                        outlined
                    ></v-text-field>
                </v-col>
                <v-col cols="6" class="pr-2">
                    <v-text-field
                        :value="canEditName ? account_member.name : nonDirtyAccountMember.name"
                        :error-messages="account_member.name_error"
                        :label="$t('First name')"
                        :disabled="!canEditName"
                        outlined
                        @input="account_member.name = $event"
                    ></v-text-field>
                </v-col>
                <v-col cols="6" class="pl-2">
                    <v-text-field
                        :value="canEditName ? account_member.surname : nonDirtyAccountMember.surname"
                        :error-messages="account_member.surname_error"
                        :label="$t('Last name')"
                        :disabled="!canEditName"
                        outlined
                        @input="account_member.surname = $event"
                    ></v-text-field>
                </v-col>
                <v-col cols="12">
                    <v-text-field
                        :value="account_member.ext_user_id"
                        :label="$t('External ID')"
                        outlined
                        @input="account_member.ext_user_id = $event"
                    ></v-text-field>
                </v-col>
                <v-col v-if="groups.length > 0" cols="12">
                    <ComboboxDropdown
                        name-key="name"
                        value-key="id"
                        show-selected-first
                        :items="groups"
                        :selected="groups.filter(x => canEditPerson ? account_member.group_ids.includes(x.id) : nonDirtyAccountMember.group_ids.includes(x.id))"
                        :label="$t('Select groups')"
                        :disabled="!canEditPerson"
                        multi
                        @input="account_member.group_ids = $event.map(x => x.id)"
                    />
                </v-col>
                <v-col v-if="admin_group" cols="12" class="pl-2 pb-2">
                    <v-checkbox
                        :input-value="canEditPerson ? account_member.is_admin : nonDirtyAccountMember.is_admin"
                        color="primary"
                        :error-messages="account_member.is_admin_error"
                        :hide-details="!account_member.is_admin_error"
                        :label="$t('Administrator')"
                        :disabled="!canEditPerson"
                        @change="account_member.is_admin = $event"
                    ></v-checkbox>
                </v-col>
            </v-row>
        </form>
        <template slot="footer">
            <danger-button
                v-if="currentStatus === 'limited' && account_member && account_member.person_id"
                @click="deactivateMemberDialogVisible = true"
            >
                {{ $t('Delete') }}
            </danger-button>
            <v-spacer />
            <primary-button
                :loading="loading"
                :disabled="account_member && account_member.status === 'inactive' && currentStatus === 'inactive'"
                :color="account_member && account_member.status === 'inactive' ? 'error' : 'primary'"
                @click="onSaveClick"
            >
                {{ buttonText }}
            </primary-button>
        </template>
        <DeactivateMemberDialog
            v-if="deactivateMemberDialogVisible"
            :edit-person-id="account_member.person_id"
            :loading="deactivateLoading"
            :company-id="companyId"
            :selected-member-id="selectedDeactivateMemberId"
            :deactivate-member="account_member"
            @member-change="selectedDeactivateMemberId = $event"
            @deactivate="onDeactivateClick()"
            @close="deactivateMemberDialogVisible = false"
        />
    </Dialog>
</template>

<script>
import Vue from "vue";
import GeneralDialogTemplate from "../Dialog/GeneralDialogTemplate";
import DialogModule from "../../store/modules/dialog";
import UserModule from "../../store/modules/user";
import AppModule from "../../store/modules/app";
import {computed, ref, onMounted} from "@vue/composition-api";
import ComboboxDropdown from "../../components/ComboboxDropdown";
import { $t } from "@/i18n";
import errorDialog from "@/views/ErrorDialog/errorDialog"
import DeactivateMemberDialog from "@/views/Users/DeactivateMemberDialog";
import {uku_send_event, UKU_ACTIVITIES} from "@/helpers/uku_activity";
import Dialog from "@/components/Dialog";
import FeedbackNotificationModule from "@/store/modules/feedback_notification";

export default {
    name: "AccountMemberEditDialog",
    components: {
        GeneralDialogTemplate,
        ComboboxDropdown,
        DeactivateMemberDialog,
        Dialog
    },
    props: {
        companyId: {
            type: Number,
            required: true
        },
        personId: {
            type: Number,
            default: null
        }
    },
    setup(props, ctx) {
        const { handleErrorResponse, clearValidationErrors, fetchClassifiers, account_groups } = AppModule();
        const { fetchMember, updateMember } = UserModule();
        const { showFeedbackNotification } = FeedbackNotificationModule();

        const deactivateLoading = ref(false);
        const deactivateMemberDialogVisible = ref(false);
        const selectedDeactivateMemberId = ref(null);
        const account_member = ref(null);
        const nonDirtyAccountMember = ref(null);
        const loading = ref(false);
        const groups_loaded = ref(false);
        const show_owner = ref(false);
        const currentStatus = ref('limited')

        const groups = computed(() => {
            if (account_groups.value[props.companyId] === undefined)
                return [];
            return account_groups.value[props.companyId].filter(
                x => x.data == null || x.data.group_type === undefined
            );
        });
        const admin_group = computed(() => {
            if (account_groups.value[props.companyId] === undefined)
                return null;
            return account_groups.value[props.companyId].find(
                x => x.data != null && x.data.group_type !== undefined && x.data.group_type === "administrators"
            );
        });
        const is_admin = computed(() => {
            if (admin_group.value === null || account_member.value === null || account_member.value.group_ids === null) return false;
            return account_member.value.group_ids.indexOf(admin_group.value.id) >= 0;
        });

        const canEditEmail = computed(() => {
            return account_member.value.user_status === 'inactive' && ['limited', 'pending', 'active'].includes(account_member.value.status) && account_member.value.can_edit_email;
        });

        const canEditName = computed(() => {
            return (account_member.value.user_status === 'inactive' && ['limited', 'pending', 'active'].includes(account_member.value.status));
        })

        const canEditPerson = computed(() => {
            return ['active', 'limited', 'pending'].includes(account_member.value.status);
        })

        const load_data = async function() {
            if (props.personId) {
                try {
                    const resp = await fetchMember(props.companyId, props.personId)
                    account_member.value = prepareFormData(resp);

                    currentStatus.value = account_member.value.status
                } catch (e) {
                    errorDialog.showError(e)
                }
            } else {
                account_member.value = prepareFormData({
                    name: "",
                    surname: "",
                    role: "user",
                    status: "limited",
                    email: "",
                    company_id: props.companyId,
                    user_status: 'inactive',
                    group_ids: [],
                    can_edit_email: true,
                    ext_user_id: ""
                });
            }

            nonDirtyAccountMember.value = { ...account_member.value }

            try {
                await fetchClassifiers({ types: ["account_groups"], account_id: props.companyId })
            } catch (e) {
                errorDialog.showError(e)
            }

            groups_loaded.value = true;
            if (account_member.value != null && account_member.value.group_ids != null && admin_group.value != null) {
                account_member.value.is_admin = account_member.value.group_ids.indexOf(admin_group.value.id) >= 0;
            }
        };

        const prepareFormData = function(data) {
            data = Vue.prepare_form_data(data, {
                "status": "choice_single",
                "name": "text",
                "surname": "text",
                "role": "choice_single",
                "email": "email",
                "active": "boolean",
                "group_ids": "list",
                "is_admin": "boolean",
                "ext_user_id": "text"
            });
            data.active = data.status === "pending" || data.status === "active";

            if (data.role) {
                if (data.role === "owner") {
                    data.is_admin = true;
                    show_owner.value = true;
                }
            }
            if (admin_group.value != null && data.group_ids !== null) {
                data.is_admin = data.group_ids.indexOf(admin_group.value.id) >= 0;
            }

            return data;
        };

        onMounted(() => {
            load_data()
        })

        const handleSave = (selectedDeactivateMemberId, deactivate) => {
            clearValidationErrors(account_member.value);
            loading.value = true;

            let group_ids = account_member.value.group_ids || [];
            if (admin_group.value != null) {
                if (account_member.value.is_admin) {
                    if (group_ids.indexOf(admin_group.value.id) === -1)
                        group_ids.push(admin_group.value.id);
                }
                else {
                    if (group_ids.indexOf(admin_group.value.id) >= 0)
                        group_ids.splice(group_ids.indexOf(admin_group.value.id), 1);
                }
            }

            // notice :: hack - admin role is in conflict with admin group, I don't know which is the truth
            if (account_member.value.role !== 'owner') {
                if (account_member.value.is_admin){
                    account_member.value.role = 'admin'
                } else {
                    account_member.value.role = 'user'
                }
            }

            let data = {
                id: account_member.value.id,
                full_name: account_member.value.full_name,
                name: account_member.value.name,
                surname: account_member.value.surname,
                role: account_member.value.role,
                email: account_member.value.email,
                company_id: props.companyId,
                person_id: props.personId,
                group_ids: group_ids,
                status: deactivate ? 'inactive' : account_member.value.status,
                new_person_id: selectedDeactivateMemberId,
                ext_user_id: account_member.value.ext_user_id
            };

            updateMember(props.companyId, data)
                .then(function(resp) {
                    if (resp.send_member_invitation) {
                        uku_send_event({ event: UKU_ACTIVITIES.INVITE_SENT, info: "EditAccountMemberDialog.onSaveClick"})
                        showFeedbackNotification($t('Invite sent'))
                    }
                    ctx.emit('close')
                    ctx.emit('saved')

                    if (nonDirtyAccountMember.value.email !== data.email) {
                        ctx.emit('clear-email-sent', data.id)
                    }
                })
                .catch(function(ex) {
                    loading.value = false;
                    deactivateLoading.value = false;
                    handleErrorResponse(account_member.value, ex);
                });
        }

        const onSaveClick = function() {
            if (account_member.value.status === 'inactive') {
                deactivateMemberDialogVisible.value = true
                return
            }

            handleSave()
        };

        const onDeactivateClick = async function() {
            deactivateLoading.value = true
            await handleSave(selectedDeactivateMemberId.value, true)
        };

        const buttonText = computed(() => {
            if (!props.personId) {
                return $t('Add member')
            } else if (account_member.value && account_member.value.status === 'active' && currentStatus.value !== 'active') {
                return $t('Activate member')
            } else if (account_member.value && account_member.value.status === 'inactive' && currentStatus.value !== 'inactive') {
                return $t('Deactivate member')
            }

            return $t('Save')
        })

        const statusOptions = computed(() => {
            const options = []
            if (currentStatus.value === 'pending') {
                options.push({
                    value: 'pending',
                    name: $t('Invited'),
                    description: [
                        $t('Invite sent'),
                        $t('Paid member')
                    ]
                })
            } else {
                options.push({
                    value: 'active',
                    name: $t('Active'),
                    description: [
                        $t('Send an invitation'),
                        $t('Paid member')
                    ]
                })
            }

            if (currentStatus.value === 'limited') {
                options.push({
                    value: 'limited',
                    name: $t('Limited'),
                    description: [
                        $t('No invite, no access'),
                        $t('Free until activated')
                    ]
                })
            } else {
                options.push({
                    value: 'inactive',
                    name: $t('Inactive'),
                    description: [
                        $t('No access, no charge'),
                        $t('Hidden member')
                    ]
                })
            }

            return options
        })

        return {
            account_member,
            loading,
            groups_loaded,
            canEditEmail,
            groups,
            admin_group,
            is_admin,
            onSaveClick,
            onDeactivateClick,
            selectedDeactivateMemberId,
            deactivateMemberDialogVisible,
            deactivateLoading,
            currentStatus,
            buttonText,
            canEditName,
            statusOptions,
            nonDirtyAccountMember,
            canEditPerson
        };
    }
};
</script>

<style scoped lang="scss">
.v-radio {
    align-items: flex-start !important;
    ::v-deep .v-label {
        margin-top: 2px;
    }
}
</style>